import { useState, useEffect } from 'react';
import { Clock, AlertTriangle, CheckCircle, Timer, Square, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { parseLADateTime, nowLA } from '../../utils/laTimezone';
import { zonaIANA } from '../../utils/zonaHoraria';

function formatTime(date) {
  if (!date) return '--:--';
  return date.toLocaleTimeString('en-US', { timeZone: zonaIANA(), hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatDuration(ms) {
  if (ms < 0) ms = 0;
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const s = Math.floor((ms % 60000) / 1000);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}

function parseTimeToDate(timeStr, baseDate) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  const d = new Date(baseDate);
  d.setHours(h, m, 0, 0);
  return d;
}

export default function SmartTimerBar({ citaActiva, siguienteCita, onComplete }) {
  const { t: tClinical } = useTranslation('clinical');
  const { t: tPatients } = useTranslation('patients');  const [now, setNow] = useState(() => nowLA());

  useEffect(() => {
    const interval = setInterval(() => setNow(nowLA()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!citaActiva) return null;

  const inicioAtencion = citaActiva.inicioAtencion
    ? parseLADateTime(citaActiva.inicioAtencion)
    : null;

  const sessionDuration = inicioAtencion ? now - inicioAtencion : 0;
  const sessionStart = inicioAtencion ? formatTime(inicioAtencion) : '--:--';

  const duracion = citaActiva.duracionMinutos || 30;
  const finSesion = inicioAtencion ? new Date(inicioAtencion.getTime() + duracion * 60000) : null;
  const sessionRemainingMs = finSesion ? finSesion - now : null;
  const sessionExpired = sessionRemainingMs !== null && sessionRemainingMs <= 0;

  let nextCitaTime = null;
  let nextCitaLabel = '';
  let timeUntilNext = Infinity;

  if (siguienteCita) {
    nextCitaTime = parseTimeToDate(siguienteCita.hora, now);
    if (nextCitaTime && nextCitaTime <= now) {
      nextCitaTime.setDate(nextCitaTime.getDate() + 1);
    }
    if (nextCitaTime) {
      timeUntilNext = nextCitaTime - now;
      nextCitaLabel = formatTime(nextCitaTime);
    }
  }

  const remainingMs = nextCitaTime ? timeUntilNext : null;
  const remainingMin = remainingMs !== null ? Math.floor(remainingMs / 60000) : null;

  let status = 'normal';
  let statusLabel = tClinical('normal');
  let statusIcon = CheckCircle;

  if (sessionExpired) {
    status = 'expired';
    statusLabel = tClinical('tiempo_expirado');
    statusIcon = AlertTriangle;
  } else if (sessionRemainingMs !== null) {
    const remainingSessionMin = Math.floor(sessionRemainingMs / 60000);
    if (remainingSessionMin < 5) {
      status = 'urgent';
      statusLabel = tClinical('urgente');
      statusIcon = AlertTriangle;
    } else if (remainingSessionMin < 15) {
      status = 'warning';
      statusLabel = tClinical('precaucion');
      statusIcon = AlertTriangle;
    }
  } else if (remainingMin !== null) {
    if (remainingMin < 5) {
      status = 'urgent';
      statusLabel = tClinical('urgente');
      statusIcon = AlertTriangle;
    } else if (remainingMin < 15) {
      status = 'warning';
      statusLabel = tClinical('precaucion');
      statusIcon = AlertTriangle;
    }
  }

  const StatusIcon = statusIcon;

  return (
    <div className={`smart-timer smart-timer--${status}`}>
      {citaActiva.pacienteNombre && (
        <div className="smart-timer__section smart-timer__section--cliente">
          <User size={16} />
          <span className="smart-timer__cliente-nombre">{citaActiva.pacienteNombre}</span>
          <span className="smart-timer__label">{tPatients('cliente')}</span>
        </div>
      )}
      <div className="smart-timer__divider" />

      <div className="smart-timer__section smart-timer__section--clock">
        <Clock size={16} />
        <span className="smart-timer__time">{formatTime(now)}</span>
        <span className="smart-timer__label">{tClinical('ahora')}</span>
      </div>

      <div className="smart-timer__divider" />

      <div className="smart-timer__section smart-timer__section--session">
        <Timer size={16} />
        <span className="smart-timer__time">{formatDuration(sessionDuration)}</span>
        <span className="smart-timer__label">{tPatients('sesion')} ({sessionStart})</span>
      </div>

      {sessionRemainingMs !== null && (
        <>
          <div className="smart-timer__divider" />
          <div className="smart-timer__section smart-timer__section--remaining">
            <Timer size={16} />
            <span className="smart-timer__time">
              {sessionExpired ? formatDuration(Math.abs(sessionRemainingMs)) : formatDuration(sessionRemainingMs)}
            </span>
            <span className="smart-timer__label">
              {sessionExpired ? tClinical('tiempo_expirado') : tClinical('tiempo_restante')}
            </span>
          </div>
        </>
      )}

      {nextCitaTime && (
        <>
          <div className="smart-timer__divider" />
          <div className="smart-timer__section smart-timer__section--next">
            <StatusIcon size={16} />
            <span className="smart-timer__time">{nextCitaLabel}</span>
            <span className="smart-timer__label">
              {tPatients('siguiente_cita_restantes', { tiempo: remainingMs !== null ? formatDuration(remainingMs) : '' })}
            </span>
          </div>
        </>
      )}

      <div className={`smart-timer__badge smart-timer__badge--${status}`}>
        <StatusIcon size={14} />
        <span>{statusLabel}</span>
      </div>

      {onComplete && (
        <button className="smart-timer__complete-btn" onClick={onComplete}>
          <CheckCircle size={14} /> {tClinical('completar_cita')}
        </button>
      )}
    </div>
  );
}
